import {
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Typography,
    Tooltip,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useMemo, useState } from 'react';
import { Card as BCard } from 'react-bootstrap';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { CustomLabel, Loadable, PopoverLink } from '../../../../common';
import { SummaryTile } from '../../../../common/widgets/SummaryTile';
import { asDate } from '../../../../util/date';
import { ClaimHistory } from '../../ClaimHistory';
import { AdvancedTrace } from '../AdvancedTrace';
import { ArchivedClaimDetails } from '../ArchivedClaimDetails';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { ChangeStatusComponent } from '../UpdateStatusComponent';
import AssignClaimSection from '../AssignClaim/AssignClaimSection';
import AssignRCMTeamSection from '../AssignRCMTeamSection/AssignRCMTeamSection';
import InfoIcon from '@mui/icons-material/Info';
import { asDollarsWithZero } from '../../../../util/format';
import { useData } from '@pure-ptr/react';
import { ContractInfo } from '../../../../store/api/ai-api';
import PatientDetail from '../PatientDetails/PatientDetails';
import { checkIfFHIRConfigExists, getClaimSubmissionHistory, getOptumClaimStatus } from '../../../../store/api/clients';
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import ClaimAttachmentDialog from './ClaimAttachmentDialog/ClaimAttachmentDialog';

const useStyles = makeStyles(theme => {
    return {
        mainDiv: {
            padding: '16px 24px',
        },
        sectionHeader: {
            paddingLeft: '28px',
            minHeight: '48px',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center'
        },
        sectionCard: {
            borderRadius: '8px !important',
            height: '100%'
        },
        sectionHeaderTab: {
            padding: '0 28px',
            backgroundColor: '#EBFFF5',
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            },
            [theme.breakpoints.down('sm')]: {
                padding: '0 16px',
                '& .MuiTabs-flexContainer': {
                    flexWrap: 'nowrap !important',
                }
            },
        },
         box: {
            padding: '0px 20px',
            "& b": {
                '@media (max-width: 1295px)': {
                    fontSize: '12px !important'
                },
                '@media (max-width: 1240px)': {
                    fontSize: '11px !important'
                },
                '@media (max-width: 600px)': {
                    padding: '0px 8px',
                },
            },
        },
        tabs: {
            '& button': {
                minWidth: 'unset !important',
                padding: '0 5px',
                textTransform: 'none !important',
                fontSize: '14px'
            },
             '& .MuiTabs-indicator': {
                minWidth: 'unset !important'
            },
            [theme.breakpoints.down('sm')]: {
            '& .MuiTabs-flexContainer': {
                flexWrap: 'nowrap',
            }
        }
        },
        tabBody: {
            padding: '0 30px',
            [theme.breakpoints.down('sm')]: {
            padding: '20px 16px',
        }
        },
        tabSecBorder: {
            border: '0.56px solid  rgba(0, 0, 0, 0.25)',
            marginLeft: '-2px'
        },
        tabSecLeft: {
            paddingRight: '30px !important',
            '@media (max-width: 600px)': {
                paddingRight: '0 !important',
                paddingBottom: '24px',
            },
        },
        tabSecRight: {
            paddingLeft: '30px !important',
            '@media (max-width: 600px)': {
                paddingLeft: '16px !important',
                paddingTop: '0'
            },
        },
        basicDetailsGrid: {
            '& .loadable': {
                marginBottom: '0 !important'
            }
        },
        totalExpectedPayLabel: {
            marginLeft: '0.5rem'
        },
        eapgGroupsSummary: {
            maxHeight: '260px',
            overflowY: 'auto',
        },
        eapgIssuesSummary: {
            maxHeight: '160px',
            overflowY: 'auto',
            paddingRight: '8px'
        },
        stepContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            padding: '40px 20px',
        },
        circle: {
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#888',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: 16,
            cursor: 'pointer',
        },
        date: {
            marginTop: 8,
            fontWeight: 600,
        },
        line: {
            position: 'absolute',
            top: '50%',
            left: '100%',
            transform: 'translate(-25%, -50%)',
            width: 60,
            height: 2,
            backgroundColor: '#ccc',
        },
        tooltipPaper: {
            padding: 12,
            minWidth: 800,
            maxWidth: 800,
            overflowX: 'auto',
        },
        tableHeadCell: {
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },
        tableCell: {
            whiteSpace: 'nowrap',
        },
    }
});

function BasicDetailsSection(props) {
    const ContractsApi = new ContractInfo();
    const {
        isFromModel, isFromTriage, claimAllDetails, canAccessPayerMapping,
        recoveredAmountFlag, sections = [], chargeLevelSections = [],
        reprocessClaimCallback,
        drg, drgDescription, principalDx, principalDxDescription, dischargeStatusDescription,
        getClaimDetailsPending, claimBulkOnDemandPending,
        claimId, consolidatedClaims, assignClaimsCallback,
        principalPx, principalPxDescription,
        isTraceOpen, setTraceOpen,
        isHistoryOpen, setHistoryOpen, isAttachmentDialogOpen,
        isPatientDetailsOpen, setPatientDetailsOpen,setUploadAttachmentOpen,
        actions: {
            setAdjustment, setClaimServiceAdjustments, processBulkOnDemand,
            setClaimCommentsConfirmationDialog, getConsolidatedClaims,
            getModelConsolidatedClaims, resetConsolidatedClaimsView,
            resetModelConsolidatedClaimsView, getClaimStatuses
        }
    } = props;

    const [isProcessClaimResponseDialogOpen, setClaimProcessResponseDialog] = useState(false);
    const [processClaimResponse, setProcessClaimResponse] = useState(null);
    const [tab, setTab] = React.useState(0);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [claimAnalysisId, setClaimAnalysisId] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const classes = useStyles();


    const [isFHIRConfigured, setIsFHIRConfigured] = useState(false);

    const {} = useData(async () => {
    try {
            const response = await checkIfFHIRConfigExists();
            setIsFHIRConfigured(response);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const tabConfig = useMemo(() => {
        const tabs = [
            { id: 'claim-info', label: 'Claim Info', condition: true },
            { id: 'demographics', label: 'Demographics', condition: !isFromModel },
            { id: 'remit-timeline', label: 'Remit Timeline', condition: !isFromModel },
            { id: 'version', label: 'Version', condition: !isFromModel },
            { id: 'follow-up-call', label: 'Follow-Up Call', condition: !isFromModel },
            { id: 'claim-action', label: 'Claim Action', condition: !isFromModel && claimAllDetails?.isClaimActionApplied === true },
            { 
                id: 'eapg', 
                label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        EAPG
                        <PopoverLink
                            columnData={<InfoIcon fontSize='small' color="action" />}
                            hoverData={[{
                                label: "",
                                data: "Reports are produced, in part, by using Solventum's proprietary computer software created, licensed, and/or owned by Solventum Health Information Systems, Inc. All copyrights in and to the Software licensed by Solventum Health Information Systems, Inc. hereunder are owned by Solventum or its affiliates. All rights reserved."
                            }]}
                            title={""}
                            maxWidth={800}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        />
                    </span>
                ),
                condition: !isFromModel && (claimAllDetails?.eapgClaimExecutionGroups?.length > 0 || claimAllDetails?.eapgClaimExecutionIssues?.length > 0)
            },
            { id: 'eapg-schedule', label: 'EAPG Schedule Summary', condition: !isFromModel && claimAllDetails?.eapgClaimExecutionSchedules?.length > 0 }
        ];
        return tabs.filter(t => t.condition);
    }, [
        isFromModel, 
        claimAllDetails?.isClaimActionApplied,
        claimAllDetails?.eapgClaimExecutionGroups?.length,
        claimAllDetails?.eapgClaimExecutionIssues?.length,
        claimAllDetails?.eapgClaimExecutionSchedules?.length
    ]);

    useEffect(() => {
        if (claimId) {
            isFromModel ? getModelConsolidatedClaims(claimId.replace('/', ',')) : getConsolidatedClaims(claimId)
            return () => {
                resetConsolidatedClaimsView();
                resetModelConsolidatedClaimsView();
            }
        }
    }, [claimId, isFromModel]);

    useEffect(() => {
        getClaimStatuses();
    }, [getClaimStatuses]);

    useEffect(() => {
        if (claimAllDetails?.id) {
            setClaimAnalysisId(claimAllDetails?.id);
        }
        return () => { setClaimAnalysisId(null); };
    }, [claimAllDetails?.id]);

    useEffect(() => {
    if (props.registerReprocessCallback) {
        props.registerReprocessCallback(reprocessCallback_internal);
    }
    }, []);

    const reprocessRef = React.useRef(reprocessClaimCallback);
    React.useEffect(() => {
        reprocessRef.current = reprocessClaimCallback;
    }, [reprocessClaimCallback]);

    const handleChange = (event, newValue) => { setTab(newValue); };

    const handleReprocessClaim = () => {
        const payload = {
            claimMetadata: [
                {
                claimNumber: claimAllDetails?.claimnumber,
                contractId: claimAllDetails?.contractid,
            }
        ]
        };
        processBulkOnDemand(payload, reprocessCallback_internal);
    };

    function reprocessCallback_internal(resArr) {
        const processItem = resArr.find(item => item.processingState === 'IGNORED' || item.processingState === 'FAILED');
        // display of error when processing state is ignored or failed.
        if (processItem) {
            setProcessClaimResponse(processItem);
            setClaimProcessResponseDialog(true);
        } else {
            reprocessRef.current();
        }
    }

    function handleProcessClaimDialogClose() {
        setClaimProcessResponseDialog(false);
        setProcessClaimResponse(null);

        // if status is ignored redirecting to claim analysis
        if (processClaimResponse?.processingState === 'IGNORED') {
            props.history.push(`/cms/claimAnalysis`);
        }
        else {
            // getting claim details after viewing error details
            reprocessRef.current();
        }
    }

    function renderPatientName() {
        return claimAllDetails?.patientLastname ? claimAllDetails?.patientLastname + ', ' + `${claimAllDetails?.patientFirstname ?? ''} ${claimAllDetails?.patientMiddlename ?? ''}` : ''
    }

    function renderPhysicianName() {
        return claimAllDetails?.physicianLastname ? claimAllDetails?.physicianLastname + ', ' + `${claimAllDetails?.physicianFirstname ?? ''}` : ''
    }

    function a11yProps(index) {
        return {
            id: `vertical-tab-${index}`,
            'aria-controls': `vertical-tabpanel-${index}`,
        };
    }

    const handleDialogClose = () => {
        setClaimCommentsConfirmationDialog({ open: false, state: '' });
    }

    const getStatusText = (code) => {
    const normalized = code == null ? "" : String(code);

    switch (normalized) {
        case "1":
        case "19":
        return "Primary";
        case "22":
        return "Reversal";
        case "4":
        return "Denial";
        default:
        return normalized || "Unknown";
    }
    };

    const getRemarks = (item) => {
        return [
            item.remarkCode03,
            item.remarkCode04,
            item.remarkCode05,
            item.remarkCode06,
            item.remarkCode07,
        ].filter(Boolean).join(', ');
    };

    const getCasAdjustments = (item) => {
        return item?.casCodes?.segment?.map(seg => `${seg.groupcode}${seg.reasoncode} - ${asDollarsWithZero(seg.amount)}`).filter(Boolean).join(', ');
    }

    const {data: claimSubmissionHistory, isPending: isClaimHistoryPending } = useData(
        async () => {
            if (!claimAnalysisId) return null;
            if (tabConfig[tab]?.id !== 'claim-action') return null;
            return await getClaimSubmissionHistory(claimAllDetails?.claimnumber);
        },
        [claimAnalysisId, refreshCounter, tab, tabConfig]
    );

    const handleRefreshClaimStatus = async () => {
    if (!claimAllDetails?.claimnumber) return;
    setIsRefreshing(true);
    try {
        await getOptumClaimStatus(claimAllDetails?.claimnumber);
        setRefreshCounter(prev => prev + 1);

    } catch (error) {
        console.error("Failed to refresh claim status", error);
         toast.error(
            error?.response?.data?.message ||
            error?.response?.data ||
            "Failed to refresh claim status. Please try again.",
            {
                autoClose: 3000,
            }
        );
    }
    finally {
        setIsRefreshing(false);
    }
};
    const handleNextClaimConfirm = props.handleNextClaimConfirm ?? (() => {});
    const handlePreviousClaimConfirm = props.handlePreviousClaimConfirm ?? (() => {});

    return (
        <Grid className={classes.basicDetailsGrid}>
            <Loadable loading={getClaimDetailsPending || claimBulkOnDemandPending}>
                {/* titleDiv removed — now rendered as <ClaimViewHeader> in ClaimViewPage */}
                <Grid className={classes.mainDiv}>
                    <Grid container spacing={3}>
                        <Grid xs={12} sm={12} md={7} lg={7} xl={8} item>
                            <BCard className={classes.sectionCard}>
                                <BCard.Header as="h5" className={classes.sectionHeaderTab}>
                                    <Tabs
                                        value={tab}
                                        onChange={handleChange}
                                        aria-label="Claim details Tab"
                                        className={classes.tabs}
                                        variant="scrollable"
                                    >
                                        {tabConfig.map((tabItem, index) => (
                                            <Tab 
                                                key={tabItem.id}
                                                label={tabItem.label} 
                                                {...a11yProps(index)}
                                                className={index > 0 ? 'pl-3' : ''}
                                            />
                                        ))}
                                    </Tabs>
                                </BCard.Header>
                                <BCard.Body className='px-0'>
                                    <Collapse in={true} timeout="auto" unmountOnExit>
                                        <Box marginTop={1} marginBottom={1}>
                                            <Grid className={classes.tabBody}>
                                                {tabConfig[tab]?.id === 'claim-info' && (
                                                    <>
                                                    {isFromModel &&
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6} className={classes.tabSecLeft}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Visit" data={claimAllDetails?.visit} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="DRG" data={drg} />
                                                                </Grid>
                                                                <hr />
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Principal DX" data={principalDx} />
                                                                </Grid>
                                                            </Grid>
                                                            <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                            <Grid item xs={12} sm={6} className={classes.tabSecRight}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Service Date" data={asDate(claimAllDetails?.servicedate) || 'N/A'} />
                                                                </Grid>
                                                                <hr />
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Updated At" data={asDate(claimAllDetails?.updatedAt, 'date-time') || 'N/A'} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Updated By" data={claimAllDetails?.updatedBy} />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                    {!isFromModel &&
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6} className={classes.tabSecLeft}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Visit" data={claimAllDetails?.visit} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Denial" data={claimAllDetails?.isDenial ? <span className='text-danger'><b>Yes</b></span> : 'No'} />
                                                                </Grid>
                                                                <hr />
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Is Paid" data={claimAllDetails?.isPaid ? 'Yes' : 'No'} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="DRG"
                                                                    data={<PopoverLink columnData={drg} hoverData={[{ label: "", data: drgDescription }]} title={drg} />}
                                                                        />
                                                                    </Grid>
                                                                    {principalPx &&
                                                                        <Grid className='d-flex justify-content-between'>
                                                                            <CustomLabel label="Principal PX"
                                                                                data={<PopoverLink columnData={principalPx} hoverData={[{ label: "", data: principalPxDescription }]} title={principalPx} />}
                                                                            />
                                                                        </Grid>
                                                                    }
                                                                    <hr />
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label="Principal DX"
                                                                            data={<PopoverLink columnData={principalDx} hoverData={[{ label: "", data: props.principalDxDescription }]} title={principalDx} />}
                                                                        />
                                                                    </Grid>
                                                                    <hr />
                                                                <AssignClaimSection claimAllDetails={claimAllDetails} assignClaimsCallback={assignClaimsCallback} />
                                                                {canAccessPayerMapping &&
                                                                    <Grid className='d-flex justify-content-between' sx={{ mt: "20px" }}>
                                                                        <CustomLabel
                                                                            label="Processed Through Payer Mapping"
                                                                            data={
                                                                                <Typography>{claimAllDetails?.usePlanIdMapping ? "Yes" : "No"}</Typography>

                                                                            }
                                                                            type="claim-contract"
                                                                        />
                                                                    </Grid>}


                                                                </Grid>
                                                                <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                                <Grid item xs={12} sm={6} className={classes.tabSecRight}>
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        {claimAllDetails?.visit === "INPATIENT"
                                                                            ? <CustomLabel label="Service Date" data={`${asDate(claimAllDetails?.fromDate) || 'N/A'} - ${asDate(claimAllDetails?.throughDate) || 'N/A'}`} />
                                                                            : <CustomLabel label="Service Date" data={asDate(claimAllDetails?.servicedate) || 'N/A'} />
                                                                        }
                                                                    </Grid>
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label="Remit Date" data={asDate(claimAllDetails?.remitDate) || 'N/A'} />
                                                                    </Grid>
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label="Acknowledged Date" data={asDate(claimAllDetails?.acknowledgementDate) || 'N/A'} />
                                                                    </Grid>
                                                                    <hr />
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label="Updated At" data={asDate(claimAllDetails?.updatedAt, 'date-time') || 'N/A'} />
                                                                    </Grid>
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label="Updated By" data={claimAllDetails?.updatedBy} />
                                                                    </Grid>
                                                                    <ChangeStatusComponent claimAnalysis={claimAllDetails} setShouldReloadGrid={false} handleReprocessClaim={handleReprocessClaim} reprocessCallback={reprocessCallback_internal} />
                                                                    <AssignRCMTeamSection claimAllDetails={claimAllDetails} assignClaimsCallback={assignClaimsCallback} />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    </>
                                                )}

                                                {tabConfig[tab]?.id === 'demographics' && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Name" data={renderPatientName()} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Gender" data={claimAllDetails?.patientGender} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient City" data={claimAllDetails?.patientCity} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient State" data={claimAllDetails?.patientState} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Zip" data={claimAllDetails?.patientZip} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient MRN" data={claimAllDetails?.patientMrn} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient DOB" data={asDate(claimAllDetails?.patientDob)} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Age" data={claimAllDetails?.age} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Physician Name" data={renderPhysicianName()} />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {tabConfig[tab]?.id === 'remit-timeline' && (
                                                    <Box sx={{ overflowX: 'auto', width: '100%' }}>
                                                        {claimAllDetails?.remitResponse?.remitGroups?.length > 0 ? (
                                                            <Grid container wrap="nowrap" spacing={4}>
                                                                {claimAllDetails?.remitResponse?.remitGroups?.map((step, index) => (
                                                                    <Grid item key={`remit-timeline-${index}`} sx={{ minWidth: 100 }}>
                                                                        <Box className={classes.stepContainer}>
                                                                            <Tooltip placement="top" arrow title={
                                                                                <Paper className={classes.tooltipPaper}>
                                                                                    <Table size="small">
                                                                                        <TableHead>
                                                                                            <TableRow>
                                                                                                <TableCell className={classes.tableHeadCell}>Remit Payer Name</TableCell>
                                                                                                <TableCell className={classes.tableHeadCell}>Date</TableCell>
                                                                                                <TableCell className={classes.tableHeadCell}>Amount</TableCell>
                                                                                                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                                                                                <TableCell className={classes.tableHeadCell}>Remarks</TableCell>
                                                                                                <TableCell className={classes.tableHeadCell}>CAS Adjustments</TableCell>
                                                                                            </TableRow>
                                                                                        </TableHead>
                                                                                        <TableBody>
                                                                                            {step.remits?.map((item, i) => (
                                                                                                <TableRow key={i}>
                                                                                                    <TableCell className={classes.tableCell}>{item?.payerName}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{asDate(item.checkEftDate)}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{asDollarsWithZero(item.paidAmount)}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{getStatusText(item.claimStatusCode)}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{getRemarks(item)}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{getCasAdjustments(item)}</TableCell>
                                                                                                </TableRow>
                                                                                            ))}
                                                                                        </TableBody>
                                                                                    </Table>
                                                                                </Paper>
                                                                            } PopperProps={{ sx: { '& .MuiTooltip-tooltip': { backgroundColor: 'transparent', padding: 0 } } }}>
                                                                                <Box className={classes.circle}>{index + 1}</Box>
                                                                            </Tooltip>
                                                                            <Typography className={classes.date}>{asDate(step.checkEftDate)}</Typography>
                                                                            {claimAllDetails?.remitResponse?.remitGroups?.length > 1 && index !== claimAllDetails.remitResponse.remitGroups.length - 1 && (
                                                                                <Box className={classes.line} />
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        ) : (
                                                            <Typography variant="body1" sx={{ padding: 2 }}>No Remit timelines available.</Typography>
                                                        )}
                                                    </Box>
                                                )}

                                                {tabConfig[tab]?.id === 'version' && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Claim Analysis Version" data={claimAllDetails?.version} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Contract Version" data={claimAllDetails?.contract?.version} />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {tabConfig[tab]?.id === 'follow-up-call' && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Name" data={renderPatientName()} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient DOB" data={asDate(claimAllDetails?.patientDob)} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="DOS" data={asDate(claimAllDetails?.servicedate) || 'N/A'} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Member ID" data={claimAllDetails?.snResponsecontactidentifier} />
                                                            </Grid>

                                                             <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Discharge Status" data={<PopoverLink columnData={claimAllDetails?.dischargestatus} hoverData={[{ label: "", data: dischargeStatusDescription }]} title={claimAllDetails?.dischargestatus} />} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Payer Name" data={claimAllDetails?.usePlanIdMapping ? claimAllDetails?.planPayerContract?.payerName : claimAllDetails?.payer?.name} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Claim Amount" data={`$ ${claimAllDetails?.chargedAmount}`} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Claim Control No" data={claimAllDetails?.payerClaimControlNumber} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="NPI" data={claimAllDetails?.billingProviderNpi} />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {tabConfig[tab]?.id === 'claim-action' && (
                                                    <Box sx={{ position: 'relative', minHeight: '200px' }}>
                                                        <Box sx={{ overflowX: 'auto', width: '100%', paddingBottom: '60px' }}>
                                                            {claimSubmissionHistory && claimSubmissionHistory.length > 0 ? (
                                                                <Grid container wrap="nowrap" spacing={4}>
                                                                    {[...claimSubmissionHistory].reverse().map((historyItem, index) => (
                                                                        <Grid item key={`claim-status-timeline-${index}`}>
                                                                            <Box className={classes.stepContainer}>
                                                                                <Tooltip placement="top" arrow title={
                                                                                    <Paper className={classes.tooltipPaper}>
                                                                                        <Table size="small">
                                                                                            <TableHead>
                                                                                                <TableRow>
                                                                                                    <TableCell className={classes.tableHeadCell}>Ticket Number</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Ticket Type</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Ticket Status</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Claim Status</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Ticket Submit Date</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Date & Time</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Created By</TableCell>
                                                                                                    <TableCell className={classes.tableHeadCell}>Reason</TableCell>                                                                                                                                                                                                      
                                                                                                </TableRow>
                                                                                            </TableHead>
                                                                                            <TableBody>
                                                                                                <TableRow>
                                                                                                    <TableCell className={classes.tableCell}>{historyItem.ticketNumber || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{historyItem.ticketType || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{historyItem.ticketStatus || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{historyItem.claimStatus || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{asDate(historyItem.ticketSubmitDate, 'date-time') || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{asDate(historyItem.createdAt, 'date-time') || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{historyItem.createdBy || 'N/A'}</TableCell>
                                                                                                    <TableCell className={classes.tableCell}>{historyItem.ticketReason || 'NA'}</TableCell>                                                                    
                                                                                                </TableRow>
                                                                                            </TableBody>
                                                                                        </Table>
                                                                                    </Paper>
                                                                                } PopperProps={{ sx: { '& .MuiTooltip-tooltip': { backgroundColor: 'transparent', padding: 0 } } }}>
                                                                                    <Box className={classes.circle}>{index + 1}</Box>
                                                                                </Tooltip>
                                                                                <Typography className={classes.date}>{asDate(historyItem.createdAt) || 'N/A'}</Typography>
                                                                                {claimSubmissionHistory.length > 1 && index !== claimSubmissionHistory.length - 1 && (
                                                                                    <Box className={classes.line} />
                                                                                )}
                                                                            </Box>
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                            ) : (
                                                                <Typography variant="body1" sx={{ padding: 2 }}>
                                                                    {isClaimHistoryPending ? 'Loading claim status history...' : 'No claim status history available.'}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ position: 'absolute', top: 0, right: 0, padding: '0px' }}>
                                                            {claimAnalysisId && (
                                                                <Tooltip title="Refresh Status">
                                                                    <IconButton size="small" onClick={handleRefreshClaimStatus} disabled={isRefreshing}>
                                                                        <RefreshIcon style={{ fontSize: 20, animation: (isRefreshing || isClaimHistoryPending) ? 'spin 1s linear infinite' : 'none' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                {tabConfig[tab]?.id === 'eapg' && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} className={`${classes.tabSecLeft} ${classes.eapgGroupsSummary}`}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label={`Grouping Summary`} data={''} />
                                                            </Grid>
                                                            {claimAllDetails?.eapgClaimExecutionGroups?.length > 0 && claimAllDetails.eapgClaimExecutionGroups.map((item, index) => (
                                                                <React.Fragment key={index}>
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label={`${item?.name}`}
                                                                            data={<PopoverLink columnData={item?.code} hoverData={[{ label: "", data: item?.description }]} title={item?.code} />}
                                                                        />
                                                                    </Grid>
                                                                    <Grid className='d-flex justify-content-between'>
                                                                        <CustomLabel label={`EAPG Weight`} data={`${item?.eapgWeight}`} />
                                                                    </Grid>
                                                                    <br />
                                                                </React.Fragment>
                                                            ))}
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label={`Reimbursement Summary`} data={''} />
                                                            </Grid>
                                                            {claimAllDetails?.eapgClaimExecutionSchedules?.length > 0 &&
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Reimbursement Amount`} data={claimAllDetails?.eapgClaimExecutionSchedules[0]?.reimbursementAmount || '0.00'} />
                                                                </Grid>
                                                            }
                                                            <hr />
                                                            <Grid className={classes.eapgIssuesSummary}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Issues Reported`} data={''} />
                                                                </Grid>
                                                                {claimAllDetails?.eapgClaimExecutionIssues?.length > 0 && claimAllDetails.eapgClaimExecutionIssues.map((item, index) => (
                                                                    <Grid key={index} className='d-flex justify-content-between'>
                                                                        <Tooltip title={item?.description || ''} placement="top" arrow>
                                                                            <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                <InfoIcon className="mr-2" color="error" />
                                                                                <CustomLabel label={item?.name} data={item?.description} />
                                                                            </div>
                                                                        </Tooltip>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                {tabConfig[tab]?.id === 'eapg-schedule' && (
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label={`Schedules Summary`} data={''} />
                                                            </Grid>
                                                            {claimAllDetails?.eapgClaimExecutionSchedules?.length > 0 && claimAllDetails?.eapgClaimExecutionSchedules.map((item, index) => (
                                                                <React.Fragment key={index}>
                                                                    <Grid className='d-flex justify-content-between'><CustomLabel label={`Begin Date`} data={asDate(item?.beginDate) || 'N/A'} /></Grid>
                                                                    <Grid className='d-flex justify-content-between'><CustomLabel label={`End Date`} data={asDate(item?.endDate) || 'N/A'} /></Grid>
                                                                    <Grid className='d-flex justify-content-between'><CustomLabel label={`Description`} data={item?.description || 'N/A'} /></Grid>
                                                                    <Grid className='d-flex justify-content-between'><CustomLabel label={`Grouper Type`} data={item?.grouperTypeUsed || 'N/A'} /></Grid>
                                                                    <Grid className='d-flex justify-content-between'><CustomLabel label={`Grouper Version`} data={item?.grouperVersionUsed || 'N/A'} /></Grid>
                                                                    <Grid className='d-flex justify-content-between'><CustomLabel label={`Reimbursement Type`} data={item?.reimbursementType || 'N/A'} /></Grid>
                                                                </React.Fragment>
                                                            ))}
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder} sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
                                                        <Grid item xs={12} sm={6} className={classes.tabSecRight}></Grid>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </BCard.Body>
                            </BCard>
                        </Grid>

                        <Grid xs={12} sm={12} md={5} lg={5} xl={4} item>
                            <BCard className={classes.sectionCard}>
                                <BCard.Header as="h5" className={`${classes.sectionHeader} d-flex justify-content-between align-items-center`}>
                                    <span>Claim Stats</span>
                                    <SummaryTile isVertical={false} title="Total Variance" helpText="Difference in Expected Pay and Total allowable.  Calculated by SlicedHealth" value={claimAllDetails?.variance} color="danger" cardPadding='2px 10px' />
                                </BCard.Header>
                                <BCard.Body className='p-0'>
                                    <Collapse in={true} timeout="auto" unmountOnExit>
                                        <Box className={classes.box} marginTop={1} marginBottom={1}>
                                            <Grid container className='justify-content-between'>
                                                <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Claim Total" helpText="Total charges associated with current claim" value={claimAllDetails?.chargedAmount} color="warning" cardPadding='2px 10px' />
                                                </Grid>
                                                {!isFromModel && <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Payment" helpText="Amount paid (cash) by the current payer according to the ERA" value={claimAllDetails?.claimPaidamount} color="success" cardPadding='2px 10px' />
                                                </Grid>}
                                            </Grid>
                                            <Grid container className='justify-content-between'>
                                                {isFromModel && claimAllDetails?.totalExpectedPayNames?.length > 0 && claimAllDetails?.totalExpectedPayValues?.length > 0 && <>
                                                    <Grid item sm={12} className='pt-0'>
                                                        <b className={`kt-font-success ${classes.totalExpectedPayLabel}`}>{`Total Expected Pay:`}</b>
                                                    </Grid>
                                                    {claimAllDetails?.totalExpectedPayNames?.map((pay, index) => (
                                                        <Grid item sm={6} className='pt-0' key={index}>
                                                            <SummaryTile isVertical={true} title={pay.split('-')[0] || ''} helpText="Total amount expected to be paid by the current payer (including Patient Resp.) Calculated by SlicedHealth" value={claimAllDetails?.totalExpectedPayValues[index] || ''} color="info" cardPadding='2px 10px' />
                                                        </Grid>
                                                    ))}
                                                </>}
                                                {!isFromModel && <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Total Expected Pay" helpText="Total amount expected to be paid by the current payer (including Patient Resp.) Calculated by SlicedHealth" value={claimAllDetails?.expectedPay} color="info" cardPadding='2px 10px' />
                                                </Grid>}
                                                {!isFromModel && <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Patient Responsibility" helpText="Amount owed by the patient according to the ERA" value={claimAllDetails?.patientResponsibility} color="success" cardPadding='2px 0px 2px 10px' />
                                                </Grid>}
                                            </Grid>
                                            {!isFromModel && <Grid container className='justify-content-between'>
                                                <Grid item sm={6} className='pb-0'>
                                                    <SummaryTile isVertical={true} title="Total Non-Covered" helpText="Total non-covered amount. Calculated by SlicedHealth" value={claimAllDetails?.nonCoveredAmount} color="danger" cardPadding='2px 10px' />
                                                </Grid>
                                                <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Other Allowable" helpText="Amount adjusted by the payer that affects the allowable according to the ERA (CAS codes set in configuration screen)" value={claimAllDetails?.otherAllowable} color="success" cardPadding='2px 10px' />
                                                </Grid>
                                            </Grid>}
                                            {!isFromModel && <Grid container className='justify-content-between'>
                                                <Grid item sm={6} className='pb-0'>
                                                    {(recoveredAmountFlag === true) && (
                                                        <SummaryTile isVertical={true} title="Recovered Amount" helpText="Differences in variance between previous ERA and most recent ERA" value={claimAllDetails?.recoveredAmount} color="danger" cardPadding='2px 10px' />
                                                    )}
                                                </Grid>
                                                <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Total Allowable" helpText="Total reimbursement from current payer according to the ERA. (Payment + Patient Resp. + Other Allowable)" value={claimAllDetails?.actualPay} color="success" cardPadding='2px 10px' />
                                                </Grid>
                                            </Grid>}
                                        </Box>
                                    </Collapse>
                                </BCard.Body>
                            </BCard>
                        </Grid>
                    </Grid>
                </Grid>
            </Loadable>

            <AdvancedTrace sections={sections} isTraceOpen={isTraceOpen} setTraceOpen={setTraceOpen} title={claimAllDetails?.claimnumber} chargeLevelSections={chargeLevelSections} />
            <ClaimHistory open={isHistoryOpen} handleClose={() => setHistoryOpen(false)} claimId={claimAllDetails?.id} />
            <ArchivedClaimDetails />

            <Dialog fullWidth scroll="paper" maxWidth="xs" open={isProcessClaimResponseDialogOpen} onClose={handleProcessClaimDialogClose} aria-labelledby="form-dialog-title">
                <DialogTitle>
                    <Grid container alignItems='center'>
                        <Grid item xs={11}>Claim Processing ({processClaimResponse?.claimNumber})</Grid>
                        <Grid item xs={1} className='text-right'>
                            <IconButton aria-label="close" onClick={handleProcessClaimDialogClose} size="large"><CloseIcon /></IconButton>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid><CustomLabel label='Claim Number' data={processClaimResponse?.claimNumber} /></Grid>
                    <Grid><CustomLabel label='State' data={<b className='kt-font-danger'>{processClaimResponse?.processingState}</b>} /></Grid>
                    <Grid><CustomLabel label='Message' data={processClaimResponse?.error} /></Grid>
                </DialogContent>
            </Dialog>

            {/* ConfirmationDialog: next/prev handlers now live in ClaimViewHeader.
                ClaimViewPage passes them down via props so the dialog can still call them. */}
            <ConfirmationDialog
                handleNextClaim={handleNextClaimConfirm}
                handlePreviousClaim={handlePreviousClaimConfirm}
                handleClose={handleDialogClose}
            />

            <PatientDetail claimId={claimAllDetails?.id} isOpen={isPatientDetailsOpen} handleClose={() => setPatientDetailsOpen(false)} isFhirConfigured={isFHIRConfigured} />
            <ClaimAttachmentDialog claimId={claimAllDetails?.id} open={isAttachmentDialogOpen} handleClose={() => setUploadAttachmentOpen(false)}/>
        </Grid>
    );
}

export default withRouter(BasicDetailsSection);